/**
 * useMe — fetches `/api/me` (user + tenant + membership) for the
 * currently-authenticated session. The authenticated home page renders
 * off this hook; a 401 response (cleared by `apiClient`) means we
 * should bounce to `/login`.
 */

import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { Me } from '@/api/types-bridge'
import { getAccessToken } from '@/auth/cookie-helpers'

export const ME_QUERY_KEY = ['me'] as const

export function useMe() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      // BE wraps every response in `{ data: ... }` (Phase 2b Pass 1).
      // Unwrap here so consumers keep using `me.data.user` etc.
      const res = await apiClient<{ data: Me }>('/api/me')
      return res.data
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
    // No token → no fetch. `/api/me` is the canonical bootstrap query and
    // mounts on the home route before the auth guard redirects; gating it
    // here stops a speculative 401 on the unauthenticated landing page. The
    // query enables itself the moment a token lands (post-login).
    enabled: getAccessToken() != null,
  })
}
